import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputLabel, styled, TextField, Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import useModal from "../../hooks/useModal";
import CustomizedButton from "../ui/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadDocument } from "../../services/exitManagement";

const uploadSchema = z.object({
  documentName: z.string().min(1, "Document name is required"),
  file: z
    .instanceof(File, { message: "Document is required" })
    .refine((file) => file.size > 0, "Document cannot be empty")
    .refine(
      (file) => file.type === "application/pdf",
      "Only PDF files are allowed"
    ),
});

const VisuallyHiddenInput = styled("input")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer",
});

const UploadDocumentForm = ({ resignationId, fetchResignations, action }) => {
  const { hideModal } = useModal();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      documentName: "",
      file: null,
    },
  });

  const handleFormSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append("documentName", data.documentName);
      formData.append("document", data.file);
      formData.append("action", action);
      const response = await uploadDocument(resignationId, formData);
      if (response.status === 200) {
        toast.success(response.data.message || "Document uploaded successfully");
        hideModal();
        fetchResignations();
      } else {
        toast.error("Failed to upload document. Please try again.");
      }
      // await fetchResignations?.(); // or API call
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("An error occurred while uploading the document.");
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="pt-2">
      {/* Document Name Field */}
      <div className="row mb-3">
        <div className="col-md-12">
          <Controller
            name="documentName"
            control={control}
            render={({ field }) => (
              <TextField
                label="Document Name*"
                variant="standard"
                fullWidth
                {...field}
                error={!!errors.documentName}
                helperText={errors.documentName?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </div>
      </div>

      {/* File Upload Field */}
      <div className="row mb-3">
        <div className="col-md-12">
          <Controller
            name="file"
            control={control}
            render={({ field }) => (
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ position: "relative" }}
                >
                  <InputLabel
                    id="documents-label"
                    sx={{ minWidth: 100 }}
                    htmlFor="file-upload-input"
                  >
                    Document*
                  </InputLabel>

                  <label
                    htmlFor="file-upload-input"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <CustomizedButton
                      variant="success"
                      size="small"
                      disabled={isSubmitting}
                      aria-label="Upload file"
                      type="button"
                    >
                      <CloudUploadIcon
                        style={{ marginRight: 6, fontSize: 18 }}
                      />
                      Choose file
                    </CustomizedButton>
                    <VisuallyHiddenInput
                      id="file-upload-input"
                      type="file"
                      accept=".pdf"
                      disabled={isSubmitting}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        if (file.size > 2 * 1024 * 1024) {
                          toast.error(
                            "File size exceeds the maximum limit of 2MB."
                          );
                          return;
                        }

                        if (file.type !== "application/pdf") {
                          toast.error("Only PDF files are allowed.");
                          return;
                        }

                        field.onChange(file);
                        // if (!field.value && !getValues("documentName")) {
                        //   setValue("documentName", file.name);
                        // }
                      }}
                    />
                  </label>

                  {field.value && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 250,
                      }}
                    >
                      {field.value.name}
                    </Typography>
                  )}
                </Box>

                {/* Validation message */}
                {errors.file && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {errors.file.message}
                  </Typography>
                )}

                {/* Hint message */}
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Only PDF files are allowed (max 2MB).
                </Typography>
              </Box>
            )}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-1">
        <CustomizedButton
          variant="success"
          size="small"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </CustomizedButton>
        <CustomizedButton
          variant="light"
          size="small"
          onClick={hideModal}
          disabled={isSubmitting}
          type="button"
        >
          Cancel
        </CustomizedButton>
      </div>
    </form>
  );
};

export default UploadDocumentForm;
